import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  RefreshCw,
  Users,
  Filter,
  ChevronDown,
  ExternalLink,
  Mail,
  Phone,
  Building2,
  MessageSquare,
  Check,
  X,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Ban,
  Trophy,
  XCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// For demo purposes - in production, get from auth context
const DEMO_TOKEN = "demo_admin_token";

const STATUS_OPTIONS = [
  { value: "all", label: "All Statuses" },
  { value: "new", label: "New", icon: Clock, color: "bg-blue-500/20 text-blue-400" },
  { value: "triaged", label: "Triaged", icon: Check, color: "bg-cyan-500/20 text-cyan-400" },
  { value: "qualified", label: "Qualified", icon: CheckCircle2, color: "bg-emerald-500/20 text-emerald-400" },
  { value: "spam", label: "Spam", icon: AlertTriangle, color: "bg-yellow-500/20 text-yellow-400" },
  { value: "rejected", label: "Rejected", icon: Ban, color: "bg-red-500/20 text-red-400" },
  { value: "won", label: "Won", icon: Trophy, color: "bg-green-500/20 text-green-400" },
  { value: "lost", label: "Lost", icon: XCircle, color: "bg-zinc-500/20 text-zinc-400" },
];

const getStatusConfig = (status) => {
  return STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
};

export default function LeadsAdmin() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [filters, setFilters] = useState({
    status: "all",
    source_domain: "",
    cluster_id: "",
  });
  const [authToken, setAuthToken] = useState(localStorage.getItem('auth_token') || '');

  const fetchLeads = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append('token', authToken || DEMO_TOKEN);
      if (filters.status && filters.status !== "all") {
        params.append('status', filters.status);
      }
      if (filters.source_domain) {
        params.append('source_domain', filters.source_domain);
      }
      if (filters.cluster_id) {
        params.append('cluster_id', filters.cluster_id);
      }

      const [leadsRes, statsRes] = await Promise.all([
        axios.get(`${API}/leads?${params.toString()}`),
        axios.get(`${API}/leads/stats`)
      ]);

      setLeads(leadsRes.data.leads || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching leads:", error);
      if (error.response?.status === 403) {
        toast.error("Permission denied. Please log in as admin or editor.");
      }
    } finally {
      setLoading(false);
    }
  }, [filters, authToken]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const handleStatusUpdate = async (leadId, newStatus) => {
    try {
      await axios.patch(
        `${API}/leads/${leadId}/status?token=${authToken || DEMO_TOKEN}`,
        { status: newStatus }
      );
      toast.success(`Lead status updated to ${newStatus}`);
      fetchLeads();
      setSelectedLead(null);
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error(error.response?.data?.detail || "Failed to update status");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-full">
        <RefreshCw className="h-6 w-6 animate-spin text-zinc-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="leads-admin-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-mono font-medium text-white">Lead Triage</h1>
          <p className="text-sm text-zinc-500">v0.8.1 Admin Dashboard</p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            placeholder="Auth token..."
            value={authToken}
            onChange={(e) => {
              setAuthToken(e.target.value);
              localStorage.setItem('auth_token', e.target.value);
            }}
            className="w-48 bg-zinc-900 border-zinc-700 text-zinc-300"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={fetchLeads}
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
          >
            <RefreshCw className="h-4 w-4 mr-1" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-3">
            <div className="text-xs text-zinc-500 uppercase">Total</div>
            <div className="font-mono text-xl text-white">
              {stats?.total_submissions || 0}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-3">
            <div className="text-xs text-zinc-500 uppercase">Accepted</div>
            <div className="font-mono text-xl text-emerald-400">
              {stats?.total_accepted || 0}
            </div>
          </CardContent>
        </Card>
        {Object.entries(stats?.by_status || {}).map(([status, count]) => {
          const config = getStatusConfig(status);
          return (
            <Card key={status} className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-3">
                <div className="text-xs text-zinc-500 uppercase">{status}</div>
                <div className={`font-mono text-xl ${config.color?.split(' ')[1] || 'text-white'}`}>
                  {count}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Rejection Breakdown */}
      {stats?.by_reject_reason && Object.keys(stats.by_reject_reason).length > 0 && (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-mono text-zinc-300 flex items-center gap-2">
              <Ban className="h-4 w-4 text-red-400" />
              Rejection Reasons
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {Object.entries(stats.by_reject_reason).map(([reason, count]) => (
                <Badge key={reason} variant="outline" className="border-red-500/30 text-red-400">
                  {reason}: {count}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-mono text-zinc-300 flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Select
              value={filters.status}
              onValueChange={(value) => setFilters(f => ({ ...f, status: value }))}
            >
              <SelectTrigger className="w-40 bg-zinc-800 border-zinc-700">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              placeholder="Source Domain..."
              value={filters.source_domain}
              onChange={(e) => setFilters(f => ({ ...f, source_domain: e.target.value }))}
              className="w-48 bg-zinc-800 border-zinc-700 text-zinc-300"
            />
            <Input
              placeholder="Cluster ID..."
              value={filters.cluster_id}
              onChange={(e) => setFilters(f => ({ ...f, cluster_id: e.target.value }))}
              className="w-48 bg-zinc-800 border-zinc-700 text-zinc-300"
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setFilters({ status: "all", source_domain: "", cluster_id: "" })}
              className="text-zinc-400"
            >
              Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Leads List */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-mono text-zinc-300 flex items-center gap-2">
            <Users className="h-4 w-4" />
            Leads ({leads.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-800 text-zinc-500 text-left">
                  <th className="pb-2 pr-4">Status</th>
                  <th className="pb-2 pr-4">Contact</th>
                  <th className="pb-2 pr-4">Company</th>
                  <th className="pb-2 pr-4">Source</th>
                  <th className="pb-2 pr-4">Attribution</th>
                  <th className="pb-2 pr-4">Date</th>
                  <th className="pb-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((lead) => {
                  const statusConfig = getStatusConfig(lead.status);
                  return (
                    <tr
                      key={lead.id}
                      className="border-b border-zinc-800/50 hover:bg-zinc-800/30 cursor-pointer"
                      onClick={() => setSelectedLead(lead)}
                    >
                      <td className="py-3 pr-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-mono ${statusConfig.color}`}>
                          {statusConfig.icon && <statusConfig.icon className="h-3 w-3" />}
                          {lead.status}
                        </span>
                        {lead.reject_reason && (
                          <div className="text-xs text-red-400 mt-1">{lead.reject_reason}</div>
                        )}
                      </td>
                      <td className="py-3 pr-4">
                        <div className="text-white">{lead.name || "—"}</div>
                        <div className="text-zinc-500 text-xs">{lead.email}</div>
                      </td>
                      <td className="py-3 pr-4">
                        <div className="text-zinc-300">{lead.company || "—"}</div>
                      </td>
                      <td className="py-3 pr-4">
                        <div className="text-zinc-400 text-xs truncate max-w-[150px]" title={lead.source_domain}>
                          {lead.source_domain || "—"}
                        </div>
                      </td>
                      <td className="py-3 pr-4">
                        <div className="flex flex-wrap gap-1">
                          {lead.cluster_id && (
                            <Badge variant="outline" className="text-xs border-cyan-500/30 text-cyan-400">
                              {lead.cluster_id}
                            </Badge>
                          )}
                          {lead.utm_source && (
                            <Badge variant="outline" className="text-xs border-purple-500/30 text-purple-400">
                              {lead.utm_source}
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="py-3 pr-4">
                        <div className="text-zinc-500 text-xs font-mono">
                          {formatDate(lead.created_at)}
                        </div>
                      </td>
                      <td className="py-3">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-zinc-400 hover:text-white"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedLead(lead);
                          }}
                        >
                          <ChevronDown className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
                {leads.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-8 text-center text-zinc-500">
                      No leads found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Lead Detail Modal */}
      <Dialog open={!!selectedLead} onOpenChange={() => setSelectedLead(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-mono">Lead Details</DialogTitle>
            <DialogDescription className="text-zinc-400">
              {selectedLead?.id}
            </DialogDescription>
          </DialogHeader>

          {selectedLead && (
            <div className="space-y-4">
              {/* Status Update */}
              <div className="flex items-center gap-2 pb-4 border-b border-zinc-700">
                <span className="text-zinc-400 text-sm">Update Status:</span>
                {STATUS_OPTIONS.filter(s => s.value !== "all").map(opt => (
                  <Button
                    key={opt.value}
                    variant={selectedLead.status === opt.value ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleStatusUpdate(selectedLead.id, opt.value)}
                    className={`text-xs ${selectedLead.status === opt.value ? '' : 'border-zinc-600 text-zinc-300'}`}
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-3">
                  <h4 className="text-xs text-zinc-500 uppercase tracking-wider">Contact Info</h4>
                  <div className="flex items-center gap-2 text-sm">
                    <Users className="h-4 w-4 text-zinc-500" />
                    <span className="text-white">{selectedLead.name || "—"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4 text-zinc-500" />
                    <span className="text-zinc-300">{selectedLead.email}</span>
                    {selectedLead.is_personal_email === 1 && (
                      <Badge variant="outline" className="text-xs border-yellow-500/30 text-yellow-400">Personal</Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-zinc-500" />
                    <span className="text-zinc-300">{selectedLead.phone || "—"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Building2 className="h-4 w-4 text-zinc-500" />
                    <span className="text-zinc-300">{selectedLead.company || "—"}</span>
                  </div>
                </div>

                {/* Attribution */}
                <div className="space-y-3">
                  <h4 className="text-xs text-zinc-500 uppercase tracking-wider">Attribution</h4>
                  <div className="text-sm space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-zinc-500">Source URL:</span>
                      <span className="text-cyan-400 text-xs truncate max-w-[200px]" title={selectedLead.source_url}>
                        {selectedLead.source_url || "—"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-zinc-500">Cluster ID:</span>
                      <span className="text-purple-400">{selectedLead.cluster_id || "—"}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-zinc-500">CTA ID:</span>
                      <span className="text-emerald-400 font-mono text-xs">{selectedLead.cta_id || "—"}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-zinc-500">UTM Source:</span>
                      <span className="text-zinc-300">{selectedLead.utm_source || "—"}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-zinc-500">UTM Medium:</span>
                      <span className="text-zinc-300">{selectedLead.utm_medium || "—"}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-zinc-500">UTM Campaign:</span>
                      <span className="text-zinc-300">{selectedLead.utm_campaign || "—"}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Message */}
              {selectedLead.message && (
                <div className="space-y-2">
                  <h4 className="text-xs text-zinc-500 uppercase tracking-wider flex items-center gap-2">
                    <MessageSquare className="h-3 w-3" />
                    Message
                  </h4>
                  <div className="bg-zinc-800 rounded-md p-3 text-sm text-zinc-300">
                    {selectedLead.message}
                  </div>
                </div>
              )}

              {/* Meta Info */}
              <div className="grid grid-cols-3 gap-4 pt-4 border-t border-zinc-700 text-xs">
                <div>
                  <span className="text-zinc-500">Spam Score:</span>
                  <span className={`ml-2 ${selectedLead.spam_score >= 0.5 ? 'text-red-400' : 'text-green-400'}`}>
                    {(selectedLead.spam_score * 100).toFixed(0)}%
                  </span>
                </div>
                <div>
                  <span className="text-zinc-500">Consent:</span>
                  <span className={`ml-2 ${selectedLead.consent_checkbox ? 'text-green-400' : 'text-red-400'}`}>
                    {selectedLead.consent_checkbox ? "Yes" : "No"}
                  </span>
                </div>
                <div>
                  <span className="text-zinc-500">Created:</span>
                  <span className="ml-2 text-zinc-300">{formatDate(selectedLead.created_at)}</span>
                </div>
              </div>

              {/* Rejection Reason */}
              {selectedLead.reject_reason && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-md p-3">
                  <span className="text-red-400 text-sm font-mono">
                    Rejected: {selectedLead.reject_reason}
                  </span>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
